import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Check, Mail, Palette, Signature } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

export interface AgentEmailStatus {
  hasTemplate: boolean;
  hasBranding: boolean;
  hasSignature: boolean;
}

interface GenieCardProps {
  agent: Agent;
  isSelected: boolean;
  onSelect: () => void;
  emailStatus?: AgentEmailStatus;
  compact?: boolean;
}

const EMAIL_STATUS_INDICATORS: {
  key: keyof AgentEmailStatus;
  icon: typeof Mail;
  label: string;
}[] = [
  { key: "hasTemplate", icon: Mail, label: "Email template configured" },
  { key: "hasBranding", icon: Palette, label: "Branding enabled" },
  { key: "hasSignature", icon: Signature, label: "Email signature enabled" },
];

const GenieCard = ({ agent, isSelected, onSelect, emailStatus, compact = false }: GenieCardProps) => {
  const brand = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const activeIndicators = emailStatus ? EMAIL_STATUS_INDICATORS.filter(({ key }) => emailStatus[key]) : [];

  if (compact) {
    return (
      <div
        className={`group relative flex items-center gap-2.5 rounded-xl bg-white border-2 hover:shadow-lg transition-all duration-200 cursor-pointer pl-2.5 pr-3 py-2 h-[56px] w-fit max-w-[320px] ${
          isSelected ? "ring-2 ring-genie-primary ring-offset-2" : ""
        }`}
        onClick={onSelect}
        style={{
          borderColor: isSelected ? brand : `${brand}40`,
          background: isSelected
            ? `linear-gradient(135deg, ${brand}15 0%, white 100%)`
            : `linear-gradient(135deg, ${brand}05 0%, white 100%)`,
        }}
      >
        <div className="relative flex-shrink-0">
          <GenieAvatar
            size={32}
            color={agent.branding?.primaryColor || brand}
            customAvatarUrl={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
            secondaryColor={agent.branding?.secondaryColor}
            gradientEnabled={agent.branding?.gradientEnabled}
            gradientAngle={agent.branding?.gradientAngle}
            avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
          />
          {agent.is_active && (
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
          )}
        </div>

        <div className="min-w-[110px] max-w-[260px] relative z-10">
          <h3 className="text-sm font-medium text-gray-900 whitespace-nowrap">{agent.name}</h3>
          {activeIndicators.length > 0 && (
            <div className="flex items-center gap-1 mt-0.5">
              {activeIndicators.map(({ key, icon: Icon, label }) => (
                <Tooltip key={key}>
                  <TooltipTrigger asChild>
                    <Icon className="w-3 h-3" style={{ color: brand }} />
                  </TooltipTrigger>
                  <TooltipContent>{label}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {isSelected && (
          <div
            className="ml-auto flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center relative z-10"
            style={{ backgroundColor: brand }}
          >
            <Check className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="group relative flex flex-col items-center justify-center p-4 rounded-xl bg-white border-2 hover:shadow-lg transition-all duration-200 cursor-pointer w-[180px] min-w-[180px] h-[140px] overflow-hidden"
      onClick={onSelect}
      style={{
        borderColor: isSelected ? brand : `${brand}40`,
        background: isSelected
          ? `linear-gradient(135deg, ${brand}15 0%, white 100%)`
          : `linear-gradient(135deg, ${brand}05 0%, white 100%)`,
      }}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div
          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: brand }}
        >
          <Check className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Email configuration indicators */}
      {emailStatus && (
        <div className="absolute top-2 left-2 flex gap-1 z-10">
          {activeIndicators.map(({ key, icon: Icon, label }) => (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <div
                  className="w-5 h-5 rounded-full bg-white shadow flex items-center justify-center"
                  style={{ color: brand }}
                >
                  <Icon className="w-3 h-3" />
                </div>
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}

      {/* Subtle gradient overlay on hover */}
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${brand}10 0%, transparent 100%)`,
        }}
      />

      <div className="relative">
        <GenieAvatar
          size={64}
          color={agent.branding?.primaryColor || brand}
          customAvatarUrl={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
          secondaryColor={agent.branding?.secondaryColor}
          gradientEnabled={agent.branding?.gradientEnabled}
          gradientAngle={agent.branding?.gradientAngle}
          avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
        />
        {agent.is_active && (
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        )}
      </div>
      <h3 className="mt-3 text-sm font-medium text-gray-900 text-center truncate max-w-full px-1 relative z-10">
        {agent.name}
      </h3>
    </div>
  );
};

interface GenieShowcaseSelectorProps {
  agents: Agent[];
  selectedAgentId: string | null;
  onSelectAgent: (agentId: string | null) => void;
  showAllOption?: boolean;
  allOptionLabel?: string;
  containerId?: string;
  isLoading?: boolean;
  /** Render the "All" option inline with parent header instead of above carousel */
  renderAllInline?: boolean;
  /** Per-agent email configuration status, keyed by agent id — renders indicator badges on each card when provided */
  agentEmailStatuses?: Map<string, AgentEmailStatus>;
  /** Smaller cards + jump-to-start/end buttons, for pages where the selector is a secondary control */
  compact?: boolean;
  /** Hide the built-in jump-to-start/end row — use when the parent renders its own via GenieShowcaseJumpToStartButton/GenieShowcaseJumpToEndButton */
  renderJumpButtonsInline?: boolean;
}

/**
 * Jump-to-start/end buttons for a GenieShowcaseSelector carousel, exposed separately so a
 * parent can place them inline with its own header (pass the same `containerId` to both).
 */
const jumpToEdge = (containerId: string, edge: "start" | "end") => {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.scrollTo({
    left: edge === "start" ? 0 : container.scrollWidth,
    behavior: "smooth",
  });
};

export const GenieShowcaseJumpToStartButton = ({ containerId }: { containerId: string }) => (
  <button
    type="button"
    aria-label="Jump to first genie"
    title="Jump to first genie"
    onClick={() => jumpToEdge(containerId, "start")}
    className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-genie-primary hover:bg-gray-100 transition-colors"
  >
    <ChevronsLeft className="w-3.5 h-3.5" />
  </button>
);

export const GenieShowcaseJumpToEndButton = ({ containerId }: { containerId: string }) => (
  <button
    type="button"
    aria-label="Jump to last genie"
    title="Jump to last genie"
    onClick={() => jumpToEdge(containerId, "end")}
    className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-genie-primary hover:bg-gray-100 transition-colors"
  >
    <ChevronsRight className="w-3.5 h-3.5" />
  </button>
);

export const GenieShowcaseSelector = ({
  agents,
  selectedAgentId,
  onSelectAgent,
  showAllOption = true,
  allOptionLabel = "All Genies",
  containerId = "genie-showcase-selector-container",
  isLoading = false,
  renderAllInline = false,
  agentEmailStatuses,
  compact = false,
  renderJumpButtonsInline = false,
}: GenieShowcaseSelectorProps) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Handle scroll arrows visibility
  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const checkScroll = () => {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    };

    checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [agents, containerId]);

  const scrollContainer = (direction: "left" | "right") => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const scrollAmount = 200;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const scrollToEdge = (edge: "start" | "end") => {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.scrollTo({
      left: edge === "start" ? 0 : container.scrollWidth,
      behavior: "smooth",
    });
  };

  if (isLoading) {
    const skeletonSize = compact ? "min-w-[190px] h-[56px]" : "min-w-[180px] h-[140px]";
    return (
      <div className="flex gap-3 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`${skeletonSize} bg-gray-100 rounded-xl animate-pulse`} />
        ))}
      </div>
    );
  }

  // Render the "All" link element - can be used inline or standalone
  const allLinkElement = showAllOption && !renderAllInline ? (
    <button
      onClick={() => onSelectAgent(null)}
      className={`text-sm font-medium transition-colors ${
        selectedAgentId === null
          ? "text-genie-primary"
          : "text-gray-500 hover:text-genie-primary"
      }`}
    >
      {allOptionLabel}
      {selectedAgentId === null && (
        <span className="ml-1.5 text-xs text-genie-primary">(selected)</span>
      )}
    </button>
  ) : null;

  const jumpButtons = compact && !renderJumpButtonsInline ? (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label="Jump to first genie"
        title="Jump to first genie"
        onClick={() => scrollToEdge("start")}
        disabled={!showLeftArrow}
        className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-genie-primary hover:bg-gray-100 disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ChevronsLeft className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        aria-label="Jump to last genie"
        title="Jump to last genie"
        onClick={() => scrollToEdge("end")}
        disabled={!showRightArrow}
        className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-genie-primary hover:bg-gray-100 disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ChevronsRight className="w-3.5 h-3.5" />
      </button>
    </div>
  ) : null;

  return (
    <div className={renderAllInline ? "" : "space-y-3"}>
      {/* All Genies text link + jump buttons - only show if not rendering inline */}
      {(allLinkElement || jumpButtons) && !renderAllInline && (
        <div className="flex items-center justify-between">
          {allLinkElement}
          {jumpButtons}
        </div>
      )}

      {/* Carousel container */}
      <div className="relative">
        {/* Left scroll button */}
        {showLeftArrow && (
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-12 z-10 flex items-center"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <button
              aria-label="Scroll left"
              onClick={(e) => {
                e.stopPropagation();
                scrollContainer("left");
              }}
              className="pointer-events-auto mx-1 w-8 h-8 bg-white/95 backdrop-blur rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 active:scale-95 transition"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}

        {/* Right scroll button */}
        {showRightArrow && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-12 z-10 flex items-center justify-end"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <button
              aria-label="Scroll right"
              onClick={(e) => {
                e.stopPropagation();
                scrollContainer("right");
              }}
              className="pointer-events-auto mx-1 w-8 h-8 bg-white/95 backdrop-blur rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 active:scale-95 transition"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}

        {/* Genies Container */}
        <div
          id={containerId}
          className={`relative z-0 flex ${compact ? "gap-2" : "gap-3"} overflow-x-auto scrollbar-hide pb-2 ${showLeftArrow ? "pl-10" : "pl-0"} ${
            showRightArrow ? "pr-10" : "pr-0"
          }`}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          } as React.CSSProperties}
        >
          {/* Individual genie cards */}
          {agents.map((agent) => (
            <GenieCard
              key={agent.id}
              agent={agent}
              isSelected={selectedAgentId === agent.id}
              onSelect={() => onSelectAgent(agent.id)}
              emailStatus={agentEmailStatuses?.get(agent.id)}
              compact={compact}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
